import { useMemo } from "react";
import { Anchor } from "@mantine/core";
import { Link } from "react-router-dom";
import EcosystemHeadCorridorCard from "@/components/ecosystem-head/EcosystemHeadCorridorCard";
import type { EcosystemHeadNavState } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import {
  sectorCorridorProfileHref,
  sectorCorridorToCardData,
} from "@/pages/ecosystem-heads/ecosystemHeadCorridorCard.util";
import EcosystemCorridorPlaceholderCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPlaceholderCard";
import EcosystemCorridorPromoCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPromoCard";
import EcosystemExploreCardCarousel, {
  EcosystemExploreCarouselSlide,
} from "@/pages/ecosystem-heads/components/EcosystemExploreCardCarousel";
import { buildEcosystemCorridorEntries, ECOSYSTEM_EXPLORE_CARD_WIDTH } from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { resolvePlaceholderCorridorsForSector } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import type {
  SectorEcosystemCorridorHead,
  SectorEcosystemOverview,
} from "../../../../../../shared/sectorEcosystemOverview";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import SectorDashboardCard from "./SectorDashboardCard";

type SectorEcosystemHeadsCarouselProps = {
  sectorSlug: string;
  overview: SectorEcosystemOverview;
  headsHref: string;
  headsNavState?: EcosystemHeadNavState;
};

type CarouselCorridorItem =
  | { kind: "real"; corridor: SectorEcosystemCorridorHead }
  | { kind: "placeholder"; corridor: ComingSoonEcosystemCorridor };

export default function SectorEcosystemHeadsCarousel({
  sectorSlug,
  overview,
  headsHref,
  headsNavState,
}: SectorEcosystemHeadsCarouselProps) {
  const realCorridors = overview.ecosystemHeadCorridors;
  const hasRealCorridors = realCorridors.length > 0;
  const placeholderCorridors = useMemo(
    () => resolvePlaceholderCorridorsForSector(sectorSlug, overview.title, hasRealCorridors),
    [sectorSlug, overview.title, hasRealCorridors],
  );

  const slides = useMemo(() => {
    const realItems: CarouselCorridorItem[] = realCorridors.map((corridor) => ({
      kind: "real" as const,
      corridor,
    }));
    const placeholderItems: CarouselCorridorItem[] = placeholderCorridors.map((corridor) => ({
      kind: "placeholder" as const,
      corridor,
    }));

    return buildEcosystemCorridorEntries(realItems, placeholderItems, (item) =>
      item.kind === "real"
        ? `real-${item.corridor.ecosystemHeadUserId}-${item.corridor.country}-${item.corridor.targetCountry}`
        : `placeholder-${item.corridor.id}`,
    );
  }, [realCorridors, placeholderCorridors]);

  return (
    <SectorDashboardCard
      fillHeight={false}
      title="Ecosystem corridor"
      subtitle="Leaders driving growth in this ecosystem."
      headerAction={
        slides.length > 0 ? (
          <Anchor component={Link} to={headsHref} state={headsNavState} size="xs" fw={600} c="primary.7">
            View all corridors →
          </Anchor>
        ) : undefined
      }
    >
      {slides.length === 0 ? null : (
        <EcosystemExploreCardCarousel itemCount={slides.length}>
          {slides.map((entry) => {
            if (entry.type === "promo") {
              return (
                <EcosystemExploreCarouselSlide key={entry.key}>
                  <EcosystemCorridorPromoCard
                    variant="explore"
                    fixedWidth={ECOSYSTEM_EXPLORE_CARD_WIDTH}
                  />
                </EcosystemExploreCarouselSlide>
              );
            }

            if (entry.item.kind === "real") {
              const corridor = entry.item.corridor;
              return (
                <EcosystemExploreCarouselSlide key={entry.key}>
                  <EcosystemHeadCorridorCard
                    head={sectorCorridorToCardData(corridor)}
                    href={sectorCorridorProfileHref(corridor)}
                    linkState={headsNavState}
                  />
                </EcosystemExploreCarouselSlide>
              );
            }

            return (
              <EcosystemExploreCarouselSlide key={entry.key}>
                <EcosystemCorridorPlaceholderCard
                  corridor={entry.item.corridor}
                  variant="explore"
                  fixedWidth={ECOSYSTEM_EXPLORE_CARD_WIDTH}
                />
              </EcosystemExploreCarouselSlide>
            );
          })}
        </EcosystemExploreCardCarousel>
      )}
    </SectorDashboardCard>
  );
}
